import React from 'react';
import { Image } from 'antd';
import type { UserUpdateRequest } from '@shared/services/users.service';
import type { PersonnelDetail } from '@/types/personnelDetail';
import { buildFullUrl } from '@/contexts/AvatarContext';
import { formatDateDisplay } from '@/utils/dateHelpers';
import styles from './UpdateRequestPreview.module.css';

type ReviewCard = { id: number; documentType: string; fileUrl?: string; status?: string };

interface UpdateRequestPreviewProps {
  request: UserUpdateRequest;
  currentData: PersonnelDetail;
  currentValues: Record<string, unknown>;
  departmentNames: Record<number, string>;
  idCards: ReviewCard[];
}

const fieldLabels: Record<string, string> = {
  fullName: 'Họ và tên', employeeCode: 'Mã nhân sự', email: 'Email', phone: 'Số điện thoại', departmentId: 'Phòng ban',
  selfDescription: 'Mô tả bản thân', placeOfBirth: 'Nơi sinh', hometown: 'Quê quán',
  nationality: 'Quốc tịch', ethnicity: 'Dân tộc', dateOfBirth: 'Ngày sinh', gender: 'Giới tính',
  religion: 'Tôn giáo', maritalStatus: 'Tình trạng hôn nhân', militaryService: 'Nghĩa vụ quân sự',
  personalTaxCode: 'Mã số thuế', personalEmail: 'Email cá nhân',
  permanentAddress: 'Địa chỉ thường trú', permanentWard: 'Phường/xã thường trú',
  currentAddress: 'Địa chỉ hiện tại', currentWard: 'Phường/xã hiện tại',
  idNumber: 'Số CC/CCCD/CMT', idIssuePlace: 'Nơi cấp giấy tờ',
  idIssueDate: 'Ngày cấp giấy tờ', idExpiryDate: 'Ngày hết hạn giấy tờ',
  passportType: 'Loại hộ chiếu', passportNumber: 'Số hộ chiếu',
  passportIssuePlace: 'Nơi cấp hộ chiếu', passportIssueDate: 'Ngày cấp hộ chiếu',
  passportExpiryDate: 'Ngày hết hạn hộ chiếu', officialContractDate: 'Ngày ký hợp đồng',
  generalEducationLevel: 'Trình độ học vấn', yearsOfExperience: 'Số năm kinh nghiệm',
  specialization: 'Chuyên ngành', directManager: 'Quản lý trực tiếp', laborType: 'Loại lao động',
  company: 'Công ty', jobLocation: 'Vị trí làm việc', referencePerson: 'Người tham chiếu',
  salary: 'Mức lương', startDate: 'Ngày vào làm', position: 'Chức vụ',
  bankAccountNumber: 'Số tài khoản', bankAccountName: 'Chủ tài khoản',
  bankName: 'Ngân hàng', bankBranch: 'Chi nhánh ngân hàng',
  familyDependents: 'Người phụ thuộc', educationHistories: 'Quá trình đào tạo', workExperiences: 'Kinh nghiệm làm việc',
  certificates: 'Chứng chỉ', skills: 'Kỹ năng', horoscopeImageUrl: 'Ảnh lá số tử vi',
  relationship: 'Quan hệ', birthYear: 'Năm sinh', occupation: 'Nghề nghiệp',
  workplace: 'Nơi làm việc', address: 'Địa chỉ', fromDate: 'Từ ngày', toDate: 'Đến ngày',
  trainingType: 'Hình thức đào tạo', major: 'Chuyên ngành', educationLevel: 'Trình độ',
  school: 'Trường', certificateNumber: 'Số hiệu', issuingUnit: 'Đơn vị cấp',
  issuingBody: 'Đơn vị cấp', certificateType: 'Loại chứng chỉ', notes: 'Ghi chú',
  id_card_front: 'CC/CCCD/CMT mặt trước', id_card_back: 'CC/CCCD/CMT mặt sau',
  passport_cover: 'Bìa hộ chiếu', passport_photo: 'Ảnh hộ chiếu', passport_additional: 'Trang bổ sung hộ chiếu',
  name: 'Họ và tên', place: 'Nơi đào tạo', type: 'Loại', degree: 'Trình độ',
  time: 'Thời gian', role: 'Vị trí', title: 'Chức vụ', department: 'Phòng ban', reference: 'Người tham chiếu',
};

const dateFields = new Set([
  'dateOfBirth', 'idIssueDate', 'idExpiryDate', 'passportIssueDate', 'passportExpiryDate',
  'officialContractDate', 'startDate', 'fromDate', 'toDate',
]);

const valueLabels: Record<string, Record<string, string>> = {
  gender: { male: 'Nam', female: 'Nữ', other: 'Khác' },
  maritalStatus: { single: 'Độc thân', married: 'Đã kết hôn', divorced: 'Ly hôn', widowed: 'Góa' },
  militaryService: { yes: 'Đã hoàn thành', no: 'Hoãn nghĩa vụ', exempt: 'Miễn nghĩa vụ' },
  laborType: { 'full-time': 'Toàn thời gian', fulltime: 'Toàn thời gian', 'part-time': 'Bán thời gian', parttime: 'Bán thời gian', contract: 'Hợp đồng', intern: 'Thực tập', internship: 'Thực tập' },
  generalEducationLevel: { primary: 'Tiểu học', secondary: 'Trung học cơ sở', highschool: 'Trung học phổ thông' },
};

const currentField: Record<string, (data: PersonnelDetail) => unknown> = {
  selfDescription: (data) => data.description,
  currentAddress: (data) => data.address,
  personalTaxCode: (data) => data.taxCode,
  bankAccountNumber: (data) => data.bankAccount,
  bankAccountName: (data) => data.bankAccountHolder,
  generalEducationLevel: (data) => data.education?.highestLevel,
  specialization: (data) => data.education?.major,
  yearsOfExperience: (data) => data.education?.yearsOfExperience,
  familyDependents: (data) => data.familyMembers,
  educationHistories: (data) => data.trainingPlaces,
  certificates: (data) => data.certificates,
  skills: (data) => data.skills,
  horoscopeImageUrl: (data) => data.horoscope,
};

const labelFor = (key: string) => fieldLabels[key] || key.replace(/([a-z])([A-Z])/g, '$1 $2');

const formatScalar = (key: string, value: unknown): string => {
  if (value === null || value === undefined || value === '') return 'Chưa cập nhật';
  if (typeof value === 'boolean') return value ? 'Có' : 'Không';
  if (key === 'salary' && !Number.isNaN(Number(value))) {
    return `${new Intl.NumberFormat('vi-VN').format(Number(value))} ₫`;
  }
  if (dateFields.has(key)) return formatDateDisplay(String(value)) || String(value);
  const text = String(value);
  return valueLabels[key]?.[text.toLowerCase()] || text;
};

const formatValue = (key: string, value: unknown): React.ReactNode => {
  if (Array.isArray(value)) {
    if (value.length === 0) return 'Không có';
    return (
      <div className={styles.itemList}>
        {value.map((item, index) => (
          <div className={styles.item} key={index}>
            {typeof item === 'object' && item !== null ? (
              Object.entries(item as Record<string, unknown>)
                .filter(([, itemValue]) => itemValue !== null && itemValue !== undefined && itemValue !== '')
                .map(([itemKey, itemValue]) => (
                  <div key={itemKey}><span className={styles.itemLabel}>{labelFor(itemKey)}:</span> {formatScalar(itemKey, itemValue)}</div>
                ))
            ) : formatScalar(key, item)}
          </div>
        ))}
      </div>
    );
  }
  if (key === 'horoscopeImageUrl' && typeof value === 'string' && value) {
    return <Image src={buildFullUrl(value) || undefined} alt="Ảnh lá số tử vi" width={100} />;
  }
  if (typeof value === 'object' && value !== null) {
    return Object.entries(value).map(([itemKey, itemValue]) => (
      <div key={itemKey}><span className={styles.itemLabel}>{labelFor(itemKey)}:</span> {formatScalar(itemKey, itemValue)}</div>
    ));
  }
  return formatScalar(key, value);
};

export const UpdateRequestPreview: React.FC<UpdateRequestPreviewProps> = ({ request, currentData, currentValues, departmentNames, idCards }) => {
  const updateData = (request.updateData || {}) as Record<string, unknown>;
  const previousData = (request.previousData || {}) as Record<string, unknown>;
  const idCardIds = Array.isArray(updateData.idCardIds) ? updateData.idCardIds : [];
  const pendingCards = idCards.filter((card) => card.status?.toLowerCase() === 'pending' && idCardIds.includes(card.id));
  const certificateAttachments = Array.isArray(updateData.certificateAttachments)
    ? updateData.certificateAttachments as Array<{ certificateId: number; fileName: string; fileUrl: string }>
    : [];
  const certificateAttachmentDeleteIds = Array.isArray(updateData.certificateAttachmentDeleteIds)
    ? updateData.certificateAttachmentDeleteIds as number[]
    : [];

  const rows = Object.entries(updateData).filter(([key]) => key !== 'idCardIds' && key !== 'certificateAttachments' && key !== 'certificateAttachmentDeleteIds').map(([key, nextValue]) => {
    const oldValue = Object.prototype.hasOwnProperty.call(previousData, key) && previousData[key] !== undefined
      ? previousData[key]
      : currentValues[key] ?? currentField[key]?.(currentData) ?? (currentData as unknown as Record<string, unknown>)[key];
    return { key, oldValue, nextValue };
  });

  return (
    <section className={styles.preview} aria-label="Xem trước nội dung cập nhật">
      <div className={styles.previewHeading}>Nội dung sẽ được cập nhật ({rows.length + pendingCards.length + certificateAttachments.length + certificateAttachmentDeleteIds.length})</div>
      {rows.length === 0 && pendingCards.length === 0 && certificateAttachments.length === 0 && certificateAttachmentDeleteIds.length === 0 && <p className={styles.empty}>Không có nội dung cập nhật để xem trước.</p>}
      {rows.map(({ key, oldValue, nextValue }) => (
        <div className={styles.change} key={key}>
          <div className={styles.fieldName}>{labelFor(key)}</div>
          <div className={styles.comparison}>
            <div className={styles.valueColumn}><span className={styles.columnLabel}>Hiện tại</span><div className={styles.oldValue}>{formatValue(key, key === 'departmentId' ? departmentNames[Number(oldValue)] ?? oldValue : oldValue)}</div></div>
            <div className={styles.valueColumn}><span className={styles.columnLabel}>Sau cập nhật</span><div className={styles.newValue}>{formatValue(key, key === 'departmentId' ? departmentNames[Number(nextValue)] ?? nextValue : nextValue)}</div></div>
          </div>
        </div>
      ))}
      {pendingCards.map((card) => {
        const currentCard = idCards.find((item) => item.documentType === card.documentType && item.status?.toLowerCase() === 'approved');
        return (
          <div className={styles.change} key={`card-${card.id}`}>
            <div className={styles.fieldName}>Giấy tờ định danh: {labelFor(card.documentType)}</div>
            <div className={styles.comparison}>
              <div className={styles.valueColumn}><span className={styles.columnLabel}>Hiện tại</span><div className={styles.oldValue}>{currentCard?.fileUrl ? <Image src={buildFullUrl(currentCard.fileUrl) || undefined} alt="Giấy tờ hiện tại" width={100} /> : 'Chưa cập nhật'}</div></div>
              <div className={styles.valueColumn}><span className={styles.columnLabel}>Sau cập nhật</span><div className={styles.newValue}>{card.fileUrl ? <Image src={buildFullUrl(card.fileUrl) || undefined} alt="Giấy tờ đề xuất" width={100} /> : 'Không có ảnh'}</div></div>
            </div>
          </div>
        );
      })}
      {certificateAttachments.map((attachment) => (
        <div className={styles.change} key={`certificate-${attachment.fileUrl}`}>
          <div className={styles.fieldName}>Ảnh chứng chỉ #{attachment.certificateId}: {attachment.fileName}</div>
          <Image src={buildFullUrl(attachment.fileUrl) || undefined} alt={attachment.fileName} width={120} />
        </div>
      ))}
      {certificateAttachmentDeleteIds.map((attachmentId) => {
        const attachment = currentData.certificates?.flatMap((certificate) => certificate.attachments || []).find((item) => item.id === attachmentId);
        return (
          <div className={styles.change} key={`certificate-delete-${attachmentId}`}>
            <div className={styles.fieldName}>Xóa ảnh chứng chỉ: {attachment?.fileName || `#${attachmentId}`}</div>
            {attachment?.fileUrl && <Image src={buildFullUrl(attachment.fileUrl) || undefined} alt={attachment.fileName} width={120} />}
          </div>
        );
      })}
    </section>
  );
};
